import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, IconButton, TextField, Button, Menu, MenuItem } from '@mui/material';
import { Chat as ChatIcon, Close as CloseIcon, Send as SendIcon, Add as AddIcon } from '@mui/icons-material';
import { useRecoilValue } from 'recoil';
import { botFlowDataAtom } from '../../../Services/_states';
import MessageNode from '../../LeadWebForm/MessageNode/MessageType/MessageNode.tsx';

const ChatbotPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [rules, setRules] = useState([]);
  const [patterns, setPatterns] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [pendingRule, setPendingRule] = useState(null);
  const botFlowData = useRecoilValue(botFlowDataAtom);
  const chatContainerRef = useRef(null);
  const toggleChatbot = () => setIsOpen(!isOpen);

  useEffect(() => {
    if (isOpen) {
      const { patterns = [], rules = [] } = botFlowData;
      setRules(rules);
      setPatterns(patterns);
      if (patterns.length > 0) {
        setMessages([{ type: 'text', content: patterns[0], isIncoming: false }]);
        const firstRule = rules.find(rule => rule.is_first);
        if (firstRule) {
          setTimeout(() => simulateFlow(firstRule, rules), 1000);
        }
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const addBotMessage = (messageData) => {
    setMessages(prev => [...prev, { type: 'bot', content: messageData, isIncoming: true }]);
  };

  const simulateFlow = (startingRule, rulesList) => {
    if (!startingRule) return;
    addBotMessage(startingRule.message);
    const nextContext = startingRule?.output_context?.[0];
    if (nextContext) {
      const nextIntentId = nextContext.intent_id;
      const nextFlowId = nextContext.flow_id;
      const nextRule = nextIntentId && rulesList.find(rule => rule.intent_id === nextIntentId);
      if (nextRule) {
        setPendingRule(nextRule);
      } else if (nextFlowId) {
        const fallbackExistingNode = rulesList.find(
          rule => rule.message?.type === "EXISTINGFLOW" &&
            rule.input_context?.includes(startingRule.intent_id)
        );
        if (fallbackExistingNode) {
          addBotMessage(fallbackExistingNode.message);
          setPendingRule(null);
        } else {
          setPendingRule(null);
        }
      } else {
        setPendingRule(null);
      }
    } else {
      setPendingRule(null);
    }
  };

  const handleSendMessage = (sentMessage) => {
    const userMessage = sentMessage || message.trim();
    if (!userMessage) return;
    setMessages(prev => [...prev, { type: 'text', content: userMessage, isIncoming: false }]);
    if (pendingRule) {
      simulateFlow(pendingRule, rules);
      setPendingRule(null);
    } else {
      const matchingRule = rules.find(rule => rule.trigger?.values?.includes(userMessage));
      if (matchingRule) {
        simulateFlow(matchingRule, rules);
      }
    }
    setMessage('');
  };

  const handleButtonClick = (button) => {
    const label = button.label;
    setMessages(prev => [...prev, {
      type: 'text',
      content: label,
      isIncoming: false
    }]);
    const matchingNode = rules.find(rule => rule.intent_id === button.action.output_context);
    if (matchingNode) {
      addBotMessage(matchingNode.message);
      const nextContext = matchingNode.output_context?.[0];
      if (nextContext) {
        const nextIntentId = nextContext.intent_id;
        const nextRule = rules.find(rule => rule.intent_id === nextIntentId);
        if (nextRule) {
          setPendingRule(nextRule);
        } else {
          setPendingRule(matchingNode);
        }
      } else {
        setPendingRule(null);
      }
    }
  };

  const handleListItemClick = (option) => {
    if (option.action?.output_context) {
      handleButtonClick({
        label: option.title || option.label,
        action: option.action,
      });
    }
  };

  const handlePlusClick = (event) => setAnchorEl(event.currentTarget);

  const handleMenuClose = () => setAnchorEl(null);

  const handlePatternClick = (pattern) => handleSendMessage(pattern);

  return (
    <>
      <IconButton
        onClick={toggleChatbot}
        style={{
          position: 'fixed', bottom: '20px', right: '20px',
          backgroundColor: '#25D366', color: '#fff',
          width: '56px', height: '56px', borderRadius: '50%', zIndex: 1000,
        }}
      >
        <ChatIcon />
      </IconButton>

      {isOpen && (
        <Box
          style={{
            position: 'fixed', bottom: '20px', right: '20px',
            width: '350px', height: '500px', backgroundColor: '#F5F5F5',
            borderRadius: '20px', boxShadow: '0 6px 18px rgba(0, 0, 0, 0.2)',
            zIndex: 1001, display: 'flex', flexDirection: 'column', overflow: 'hidden',
            border: '1px solid #DDD',
          }}
        >
          <Box
            style={{
              backgroundColor: '#075E54', padding: '12px 16px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              color: '#fff', borderTopLeftRadius: '20px', borderTopRightRadius: '20px',
            }}
          >
            <Typography variant="subtitle1" style={{ fontWeight: 'bold', fontSize: '14px' }}>
              Chat Assistant
            </Typography>
            <IconButton onClick={toggleChatbot} style={{ color: '#fff', backgroundColor: 'rgba(255, 255, 255, 0.2)', padding: '6px', borderRadius: '50%' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            style={{
              flex: 1,
              padding: '8px 10px',
              backgroundColor: '#ECE5DD',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              WebkitOverflowScrolling: 'touch',
            }}
            ref={chatContainerRef}
          >
            {messages.map((message, idx) => (
              <Box
                key={idx}
                style={{
                  marginBottom: '10px',
                  display: 'flex',
                  justifyContent: message.isIncoming ? 'flex-start' : 'flex-end',
                }}
              >
                <Box
                  style={{
                    maxWidth: '70%', padding: '10px', borderRadius: '10px',
                    ...(message.isIncoming
                      ? {}
                      : {
                        backgroundColor: '#DCF8C6',
                        border: 'none',
                      }),
                    wordBreak: 'break-word',
                  }}
                >
                  {message.type === 'bot' && message.content?.type === 'EXISTINGFLOW' ? (
                    <Typography style={{
                      maxWidth: '70%',
                      padding: '10px',
                      borderRadius: '10px',
                      backgroundColor: message.isIncoming ? '#fff' : '#DCF8C6',
                      border: 'none',
                      wordBreak: 'break-word',
                    }}>EXISTING NODE</Typography>
                  ) : message.type === 'bot' ? (
                    <MessageNode
                      message={message}
                      onButtonClick={handleButtonClick}
                      onListItemClick={handleListItemClick}
                    />
                  ) : (
                    <Typography>{message.content}</Typography>
                  )}
                  {/* {message.type === 'bot' ? (
                    <MessageNode
                      message={message}
                      onButtonClick={handleButtonClick}
                      onListItemClick={handleListItemClick}
                    />
                  ) : (
                    <Typography>{message.content}</Typography>
                  )} */}


                </Box>
              </Box>
            ))}
          </Box>

          {patterns.length > 0 && (
            <Box style={{ padding: '10px', backgroundColor: '#FFF', borderTop: '1px solid #DDD', display: 'flex', gap: '8px', overflowX: 'auto' }}>
              {patterns.map((pattern, idx) => (
                <Button
                  key={idx}
                  variant="outlined"
                  style={{ borderColor: '#075E54', color: '#075E54', borderRadius: '20px', textTransform: 'none' }}
                  onClick={() => handlePatternClick(pattern)}
                >
                  {pattern}
                </Button>
              ))}
            </Box>
          )}

          <Box style={{ padding: '10px', backgroundColor: '#FFFFFF', borderTop: '1px solid #DDD', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <IconButton onClick={handlePlusClick} style={{ backgroundColor: '#ECE5DD', color: '#075E54', width: '40px', height: '40px', borderRadius: '50%' }}>
              <AddIcon />
            </IconButton>

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
              {['Send Photo/Video', 'Send Contact', 'Send Audio', 'Send Document'].map((option, index) => (
                <MenuItem key={index} onClick={handleMenuClose}>{option}</MenuItem>
              ))}
            </Menu>

            <TextField
              variant="outlined"
              size="small"
              placeholder="Type a message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              style={{ flex: 1, borderRadius: '20px', backgroundColor: '#F0F0F0' }}
              inputProps={{ style: { padding: '10px 12px', fontSize: '12px' } }}
            />

            <Button
              onClick={() => handleSendMessage()}
              variant="contained"
              style={{ backgroundColor: '#128C7E', color: '#fff', width: '40px', height: '40px', borderRadius: '50%', minWidth: '40px' }}
            >
              <SendIcon />
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
};

export default ChatbotPopup;
